import { DOCUMENT, Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/** Ancho mínimo del panel acoplado, en % del viewport */
const MIN_WIDTH = 24;
/** Ancho máximo del panel acoplado, en % del viewport */
const MAX_WIDTH = 65;
/** Ancho por defecto (deja ~66% para el contenido de la app) */
const DEFAULT_WIDTH = 34;

/** Clave de localStorage donde se recuerda el ancho elegido por el usuario */
const WIDTH_KEY = 'bryant-dock-width';
/** Clave de localStorage donde se recuerda si el panel quedó abierto */
const OPEN_KEY = 'bryant-dock-open';

/**
 * Controla el panel acoplado (dock) de Bryant.
 *
 * El panel no se monta encima del contenido: al abrirse marca `<body>` con
 * `bl-dock-open` y publica el ancho en la variable CSS `--bl-dock-w`, de forma
 * que el layout de la app se encoge y "corre" hacia la izquierda.
 *
 * Hay dos estados distintos y conviene no confundirlos:
 * - **habilitado**: si el chat tiene derecho a existir en la pantalla actual
 *   (hay sesión y la pantalla no es del tipo login/error). Lo decide
 *   `ChatDockComponent` vía `setEnabled`.
 * - **abierto**: si el usuario lo desplegó. Se recuerda entre sesiones, pero
 *   solo surte efecto cuando además está habilitado.
 */
@Injectable({ providedIn: 'root' })
export class ChatDockService {

  private _open: BehaviorSubject<boolean>;
  private _width: BehaviorSubject<number>;
  private _resizing: BehaviorSubject<boolean>;
  private _enabled: BehaviorSubject<boolean>;

  /**
   * Preferencia de apertura guardada mientras el dock está deshabilitado.
   * Sirve para devolverlo a como estaba al volver a una pantalla con sesión,
   * sin ensuciar el estado real mientras no debe verse.
   */
  private _pendingOpen: boolean;

  constructor(@Inject(DOCUMENT) private _document: Document) {
    this._open = new BehaviorSubject<boolean>(false);
    this._width = new BehaviorSubject<number>(this.readStoredWidth());
    this._resizing = new BehaviorSubject<boolean>(false);

    // Arranca deshabilitado: hasta que ChatDockComponent confirme que hay
    // sesión y que la pantalla admite el chat, no debe tocar el layout. Si no
    // fuera así, un refresh en el login pintaría el panel y correría la página.
    this._enabled = new BehaviorSubject<boolean>(false);
    this._pendingOpen = localStorage.getItem(OPEN_KEY) === 'true';

    // Publica el ancho recordado antes de la primera apertura para que la
    // transición de apertura ya arranque con el valor correcto.
    this.applyWidth(this._width.value);

    // El ancho disponible también cambia al redimensionar la ventana
    window.addEventListener('resize', () => this.syncAvailableWidth());
    this.syncAvailableWidth();
  }

  /** Emite `true` mientras el panel está acoplado y visible */
  get open$(): Observable<boolean> { return this._open.asObservable(); }

  /** Emite el ancho actual del panel, en % del viewport */
  get width$(): Observable<number> { return this._width.asObservable(); }

  /** Emite `true` mientras el usuario arrastra el borde para redimensionar */
  get resizing$(): Observable<boolean> { return this._resizing.asObservable(); }

  /** Emite `true` mientras el chat tiene derecho a existir en la pantalla */
  get enabled$(): Observable<boolean> { return this._enabled.asObservable(); }

  /** Valor actual de apertura */
  get isOpen(): boolean { return this._open.value; }

  /** Valor actual del ancho, en % del viewport */
  get width(): number { return this._width.value; }

  /**
   * Declara si el chat puede existir en la pantalla actual.
   *
   * Al deshabilitarlo se recoge todo (clase de `<body>` incluida) pero se
   * recuerda si estaba abierto, así vuelve como estaba al recuperar la sesión.
   *
   * @param {boolean} value `true` si hay sesión y la pantalla admite el chat
   */
  setEnabled(value: boolean): void {
    if (this._enabled.value === value) return;
    this._enabled.next(value);

    if (value) {
      // Restaura la preferencia guardada
      if (this._pendingOpen) {
        this._pendingOpen = false;
        this._open.next(true);
      }
    } else {
      // Se guarda la preferencia y se recoge, sin escribir en localStorage:
      // salir al login no es una decisión del usuario sobre el chat.
      this._pendingOpen = this._open.value;
      this._open.next(false);
    }

    this.applyBodyState();
    this.syncAvailableWidth();
  }

  /** Abre el panel acoplado */
  open(): void {
    if (!this._enabled.value || this._open.value) return;
    this._open.next(true);
    localStorage.setItem(OPEN_KEY, 'true');
    this.applyWidth(this._width.value);
    this.applyBodyState();
    this.syncAvailableWidth();
  }

  /** Cierra el panel y devuelve el ancho completo al contenido */
  close(): void {
    if (!this._open.value) return;
    this._open.next(false);
    localStorage.setItem(OPEN_KEY, 'false');
    this.applyBodyState();
    this.syncAvailableWidth();
  }

  /** Alterna el panel */
  toggle(): void {
    this._open.value ? this.close() : this.open();
  }

  /**
   * Cambia el ancho del panel.
   * @param {number} value Ancho deseado en % del viewport (se acota a [24, 65])
   * @param {boolean} persist Indica si debe recordarse para la próxima sesión
   */
  setWidth(value: number, persist: boolean = true): void {
    const width = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(value)));
    this._width.next(width);
    this.applyWidth(width);
    this.syncAvailableWidth();
    if (persist) {
      localStorage.setItem(WIDTH_KEY, String(width));
    }
  }

  /** Marca/desmarca el arrastre del borde (bloquea la selección de texto) */
  setResizing(value: boolean): void {
    this._resizing.next(value);
    this._document.body.classList.toggle('bl-dock-resizing', value);
  }

  /** Límites de ancho, expuestos para la UI */
  get limits(): { min: number; max: number; default: number } {
    return { min: MIN_WIDTH, max: MAX_WIDTH, default: DEFAULT_WIDTH };
  }

  /**
   * Publica en `<body>` el "breakpoint efectivo": el que corresponde al ancho
   * que realmente le queda al contenido, no al del viewport.
   *
   * Hace falta porque los `@media` de los headers miran el viewport y no saben
   * que el panel se comió parte de la pantalla: sin esto la barra sigue en modo
   * escritorio dentro de un hueco de tablet y sus elementos se atropellan.
   */
  syncAvailableWidth(): void {
    const available = window.innerWidth - (this.isPushing() ? this.dockPixels() : 0);

    const tier = available >= 1200 ? 'lg'
      : available >= 992 ? 'md'
      : available >= 768 ? 'sm'
      : 'xs';

    const classList = this._document.body.classList;
    ['lg', 'md', 'sm', 'xs'].forEach(t => classList.toggle(`bl-avail-${t}`, t === tier));
  }

  /**
   * Sincroniza la clase de `<body>` que encoge el layout.
   */
  private applyBodyState(): void {
    this._document.body.classList.toggle('bl-dock-open', this.isPushing());
  }

  /**
   * Indica si en este momento el panel le está quitando ancho al contenido.
   * @returns {boolean} `true` si está habilitado y abierto
   */
  private isPushing(): boolean {
    return this._enabled.value && this._open.value;
  }

  /**
   * Ancho del panel en píxeles. En móvil el panel es una hoja encima del
   * contenido: no le quita ancho a la app.
   * @returns {number} Píxeles que el panel le resta al contenido
   */
  private dockPixels(): number {
    // Debe coincidir con el breakpoint del SCSS del dock (max-width: 991.98px)
    if (window.innerWidth <= 991.98) return 0;
    return (window.innerWidth * this._width.value) / 100;
  }

  /**
   * Escribe el ancho en la variable CSS que consume el layout.
   * @param {number} width Ancho en % del viewport
   */
  private applyWidth(width: number): void {
    this._document.documentElement.style.setProperty('--bl-dock-w', `${width}vw`);
  }

  /**
   * Lee el ancho recordado y lo valida.
   * @returns {number} Ancho en % del viewport
   */
  private readStoredWidth(): number {
    const stored = Number(localStorage.getItem(WIDTH_KEY));
    if (!stored || Number.isNaN(stored)) return DEFAULT_WIDTH;
    return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, stored));
  }
}
